import { useMemo } from 'react';
import { format, parseISO, startOfDay, differenceInDays, isWithinInterval, startOfDay as startDay, endOfDay } from 'date-fns';

/**
 * Hook to process order data for analytics
 * @param {Array} orders - All orders including archived
 * @param {Date|null} startDate - Start date for filtering (optional)
 * @param {Date|null} endDate - End date for filtering (optional)
 * @returns {Object} Processed analytics data
 */
export const useOrderAnalytics = (orders = [], startDate = null, endDate = null) => {

  const analytics = useMemo(() => {
    if (!orders || orders.length === 0) {
      return {
        ordersByDay: [],
        revenueByDay: [],
        ordersByHour: [],
        totalOrders: 0,
        totalRevenue: 0,
        avgOrderValue: 0,
        peakOrderingHour: null,
        dateRange: null,
        productAnalytics: [],
        customerAnalytics: []
      };
    }

    // Filter out orders without valid dates
    let validOrders = orders.filter(order => order.created_at);

    // Apply date range filter if provided
    if (startDate || endDate) {
      validOrders = validOrders.filter(order => {
        try {
          const orderDate = parseISO(order.created_at);

          if (startDate && endDate) {
            return isWithinInterval(orderDate, {
              start: startDay(startDate),
              end: endOfDay(endDate)
            });
          } else if (startDate) {
            return orderDate >= startDay(startDate);
          } else if (endDate) {
            return orderDate <= endOfDay(endDate);
          }
          return true;
        } catch (e) {
          return false;
        }
      });
    }

    if (validOrders.length === 0) {
      return {
        ordersByDay: [],
        revenueByDay: [],
        ordersByHour: [],
        totalOrders: 0,
        totalRevenue: 0,
        avgOrderValue: 0,
        peakOrderingHour: null,
        dateRange: null,
        productAnalytics: [],
        customerAnalytics: []
      };
    }

    // Sort orders by date
    const sortedOrders = [...validOrders].sort((a, b) =>
      new Date(a.created_at) - new Date(b.created_at)
    );

    const firstDate = parseISO(sortedOrders[0].created_at);
    const lastDate = parseISO(sortedOrders[sortedOrders.length - 1].created_at);
    const daysDiff = differenceInDays(lastDate, firstDate);

    // Calculate totals
    const totalRevenue = validOrders.reduce((sum, order) =>
      sum + (parseFloat(order.total || order.total_price || 0)), 0
    );
    const avgOrderValue = totalRevenue / validOrders.length;

    // Group orders by day
    const ordersByDayMap = {};
    const revenueByDayMap = {};

    validOrders.forEach(order => {
      try {
        const date = parseISO(order.created_at);
        const dayKey = format(startOfDay(date), 'yyyy-MM-dd');
        const revenue = parseFloat(order.total || order.total_price || 0);

        ordersByDayMap[dayKey] = (ordersByDayMap[dayKey] || 0) + 1;
        revenueByDayMap[dayKey] = (revenueByDayMap[dayKey] || 0) + revenue;
      } catch (e) {
        console.warn('Invalid date format:', order.created_at);
      }
    });

    // Convert to array for charts
    const ordersByDay = Object.entries(ordersByDayMap)
      .map(([date, count]) => ({
        date,
        displayDate: format(parseISO(date), 'dd/MM'),
        fullDate: format(parseISO(date), 'dd/MM/yyyy'),
        count
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const revenueByDay = Object.entries(revenueByDayMap)
      .map(([date, revenue]) => ({
        date,
        displayDate: format(parseISO(date), 'dd/MM'),
        fullDate: format(parseISO(date), 'dd/MM/yyyy'),
        revenue: Math.round(revenue * 100) / 100
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Group orders by hour of day (0-23)
    const ordersByHourMap = {};

    validOrders.forEach(order => {
      try {
        const date = parseISO(order.created_at);
        const hour = date.getHours();
        ordersByHourMap[hour] = (ordersByHourMap[hour] || 0) + 1;
      } catch (e) {
        console.warn('Invalid date format:', order.created_at);
      }
    });

    // Convert to array for charts (ensure all 24 hours are present)
    const ordersByHour = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      displayHour: `${hour.toString().padStart(2, '0')}:00`,
      count: ordersByHourMap[hour] || 0
    }));

    // Find peak ordering hour
    const peakHour = ordersByHour.reduce((peak, current) =>
      current.count > peak.count ? current : peak
    );

    // PRODUCT ANALYTICS
    const productMap = {};

    validOrders.forEach(order => {
      const items = order.items || [];
      items.forEach(item => {
        const productId = item.product_id || item.id;
        const productName = item.product_name || item.name || 'Unknown Product';
        const quantity = parseInt(item.quantity) || 0;
        const itemTotal = parseFloat(item.total_price || item.unit_price * quantity || 0);

        if (!productMap[productId]) {
          productMap[productId] = {
            productId,
            productName,
            orderCount: 0,
            totalQuantity: 0,
            totalRevenue: 0
          };
        }

        productMap[productId].orderCount += 1;
        productMap[productId].totalQuantity += quantity;
        productMap[productId].totalRevenue += itemTotal;
      });
    });

    const productAnalytics = Object.values(productMap)
      .map(product => ({
        ...product,
        totalRevenue: Math.round(product.totalRevenue * 100) / 100,
        avgOrderValue: Math.round((product.totalRevenue / product.orderCount) * 100) / 100
      }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue);

    // CUSTOMER ANALYTICS
    const customerMap = {};

    validOrders.forEach(order => {
      const customerName = order.customer?.name || order.customer_name || order.user_name || 'Unknown';
      const customerPhone = order.customer?.phone || order.customer_phone || '';
      const customerEmail = order.customer?.email || order.customer_email || '';
      const orderTotal = parseFloat(order.total || order.total_price || 0);

      // Use combination of phone and name as unique identifier to handle shared phone numbers
      // If no phone, use email and name, or just name if neither exists
      const customerId = customerPhone
        ? `${customerPhone}-${customerName}`
        : customerEmail
          ? `${customerEmail}-${customerName}`
          : customerName;

      if (!customerMap[customerId]) {
        customerMap[customerId] = {
          customerId,
          customerName,
          customerPhone,
          customerEmail,
          orderCount: 0,
          totalSpent: 0,
          orders: []
        };
      }

      customerMap[customerId].orderCount += 1;
      customerMap[customerId].totalSpent += orderTotal;
      customerMap[customerId].orders.push({
        orderId: order.order_id || order.id,
        date: order.created_at,
        total: orderTotal
      });
    });

    const customerAnalytics = Object.values(customerMap)
      .map(customer => ({
        ...customer,
        totalSpent: Math.round(customer.totalSpent * 100) / 100,
        avgOrderValue: Math.round((customer.totalSpent / customer.orderCount) * 100) / 100,
        lastOrderDate: customer.orders.sort((a, b) =>
          new Date(b.date) - new Date(a.date)
        )[0]?.date
      }))
      .sort((a, b) => b.totalSpent - a.totalSpent);

    return {
      ordersByDay,
      revenueByDay,
      ordersByHour,
      totalOrders: validOrders.length,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      avgOrderValue: Math.round(avgOrderValue * 100) / 100,
      peakOrderingHour: peakHour.count > 0 ? peakHour : null,
      dateRange: {
        start: format(firstDate, 'dd/MM/yyyy'),
        end: format(lastDate, 'dd/MM/yyyy'),
        days: daysDiff + 1
      },
      productAnalytics,
      customerAnalytics
    };
  }, [orders, startDate, endDate]);

  return analytics;
};
